import { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  Button,
  useColorModeValue,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
  HStack,
  Input,
  VStack,
  Spinner,
} from '@chakra-ui/react';
import { Card } from '../../../components/Card';
import { Filter, X, Calendar, FileText } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useMeetingSummaries } from './useMeetingSummaries';
import { GenerateSummaryModal } from './components/GenerateSummaryModal';
import { MeetingCard } from './components/MeetingCard';
import { AddIcon } from '@chakra-ui/icons';
import { Timestamp } from 'firebase/firestore';
import { MeetingSummary } from '../types';

/**
 * Meeting Summaries Page - View and manage L10 meeting summaries
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function MeetingSummariesPage() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const {
    unifiedMeetings,
    loading,
    generateSummary,
    deleteSummary,
    deleteMeeting,
    exportToPDF,
  } = useMeetingSummaries(currentUser?.uid);

  const [meetingToDelete, setMeetingToDelete] = useState<{ timestamp: Timestamp; hasSummary: boolean } | null>(null);
  const [summaryToDelete, setSummaryToDelete] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const {
    isOpen: isGenerateOpen,
    onOpen: onGenerateOpen,
    onClose: onGenerateClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const filterLabelColor = useColorModeValue('gray.700', 'gray.300');
  const selectBg = useColorModeValue('white', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');

  const isSuperUser = currentUser?.role === 'super_user' || currentUser?.SuperAdmin || currentUser?.isAdmin;

  // Filter meetings by date range
  const filteredMeetings = useMemo(() => {
    if (!startDate && !endDate) return unifiedMeetings;

    return unifiedMeetings.filter((meeting) => {
      let meetingDate: Date;
      if (meeting.meetingDate instanceof Date) {
        meetingDate = meeting.meetingDate;
      } else if (meeting.meetingDate && typeof meeting.meetingDate === 'object') {
        meetingDate = (meeting.meetingDate as Timestamp).toDate();
      } else {
        meetingDate = new Date(meeting.meetingDate);
      }

      const meetingDateStr = meetingDate.toISOString().split('T')[0];
      const start = startDate || '1900-01-01';
      const end = endDate || '9999-12-31';

      return meetingDateStr >= start && meetingDateStr <= end;
    });
  }, [unifiedMeetings, startDate, endDate]);

  // Apply date filter when dates change
  useEffect(() => {
    // Filter is applied via useMemo
  }, [startDate, endDate, unifiedMeetings]);

  const setPresetRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleDeleteClick = (meetingTimestamp: Timestamp, hasSummary: boolean, summaryId?: string) => {
    if (hasSummary && summaryId) {
      setSummaryToDelete(summaryId);
      setMeetingToDelete({ timestamp: meetingTimestamp, hasSummary });
    } else {
      setMeetingToDelete({ timestamp: meetingTimestamp, hasSummary: false });
    }
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    if (meetingToDelete) {
      try {
        if (meetingToDelete.hasSummary && summaryToDelete) {
          await deleteSummary(summaryToDelete);
        } else {
          await deleteMeeting(meetingToDelete.timestamp);
        }
        toast({
          title: meetingToDelete.hasSummary ? 'Summary deleted' : 'Meeting deleted',
          status: 'success',
          duration: 3000,
        });
        setMeetingToDelete(null);
        setSummaryToDelete(null);
        onDeleteClose();
      } catch (error) {
        toast({
          title: 'Error deleting',
          description: (error as Error).message,
          status: 'error',
          duration: 5000,
        });
      }
    }
  };

  const handleGenerate = async (meetingTimestamp: Timestamp) => {
    try {
      await generateSummary(meetingTimestamp);
      toast({
        title: 'Summary generation started',
        description: 'The summary is being generated. You will see it when it\'s ready.',
        status: 'success',
        duration: 3000,
      });
      onGenerateClose();
    } catch (error) {
      toast({
        title: 'Error generating summary',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleExport = async (summary: MeetingSummary) => {
    try {
      await exportToPDF(summary);
      toast({
        title: 'Summary exported',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error exporting summary',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box textAlign="center" py={12}>
          <Spinner size="xl" />
          <Text color="gray.500" mt={4}>
            Loading meeting summaries...
          </Text>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="lg" color={headingColor} mb={2}>
            Meeting Summaries
          </Heading>
          <Text color={subheadingColor}>
            Review past L10 meetings and generate new summaries
          </Text>
        </Box>
        <HStack spacing={2}>
          <Button
            variant="outline"
            leftIcon={<Icon as={Filter} w={4} h={4} />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={onGenerateOpen}
          >
            Generate Summary
          </Button>
        </HStack>
      </Flex>

      {/* Date Range Filters */}
      {showFilters && (
        <Card bg={cardBg} p={4} mb={6}>
          <VStack align="stretch" spacing={4}>
            <HStack spacing={4} flexWrap="wrap">
              <Box flex="1" minW="200px">
                <Text color={filterLabelColor} fontWeight="medium" mb={2} fontSize="sm">
                  Start Date
                </Text>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  bg={selectBg}
                />
              </Box>
              <Box flex="1" minW="200px">
                <Text color={filterLabelColor} fontWeight="medium" mb={2} fontSize="sm">
                  End Date
                </Text>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  bg={selectBg}
                />
              </Box>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              <Button size="sm" variant="outline" onClick={() => setPresetRange(7)}>
                Last 7 Days
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPresetRange(30)}>
                Last 30 Days
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPresetRange(90)}>
                Last 90 Days
              </Button>
              {(startDate || endDate) && (
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Icon as={X} w={4} h={4} />}
                  onClick={clearDateFilter}
                >
                  Clear
                </Button>
              )}
            </HStack>
            {(startDate || endDate) && (
              <Text fontSize="sm" color={subheadingColor}>
                Showing {filteredMeetings.length} of {unifiedMeetings.length} meetings
                {startDate && ` from ${formatDate(startDate)}`}
                {endDate && ` to ${formatDate(endDate)}`}
              </Text>
            )}
          </VStack>
        </Card>
      )}

      {/* Meetings List */}
      {unifiedMeetings.length === 0 ? (
        <Card bg={cardBg} p={12}>
          <VStack spacing={4} textAlign="center">
            <Icon as={FileText} w={16} h={16} color="gray.300" />
            <Heading size="md" color={headingColor}>
              No Meetings Found
            </Heading>
            <Text color={subheadingColor}>
              Complete an L10 meeting to see it listed here.
            </Text>
          </VStack>
        </Card>
      ) : filteredMeetings.length === 0 ? (
        <Card bg={cardBg} p={12}>
          <VStack spacing={4} textAlign="center">
            <Icon as={Calendar} w={16} h={16} color="gray.300" />
            <Heading size="md" color={headingColor}>
              No Meetings in Date Range
            </Heading>
            <Text color={subheadingColor} mb={4}>
              No meetings found for the selected date range.
            </Text>
            <Button variant="outline" onClick={clearDateFilter}>
              Clear Filters
            </Button>
          </VStack>
        </Card>
      ) : (
        <VStack spacing={4} align="stretch">
          {filteredMeetings.map((meeting) => (
            <MeetingCard
              key={
                meeting.meetingTimestamp instanceof Timestamp
                  ? meeting.meetingTimestamp.toMillis().toString()
                  : String(meeting.meetingTimestamp)
              }
              meeting={meeting}
              onGenerate={() => handleGenerate(meeting.meetingTimestamp)}
              onDelete={() =>
                handleDeleteClick(
                  meeting.meetingTimestamp,
                  meeting.hasSummary,
                  meeting.summary?.id
                )
              }
              onExport={() => meeting.summary && handleExport(meeting.summary)}
              isSuperUser={isSuperUser}
            />
          ))}
        </VStack>
      )}

      {/* Modals */}
      <GenerateSummaryModal
        isOpen={isGenerateOpen}
        onClose={onGenerateClose}
        onGenerate={handleGenerate}
      />

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={undefined}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete {meetingToDelete?.hasSummary ? 'Summary' : 'Meeting'}
            </AlertDialogHeader>
            <AlertDialogBody>
              {meetingToDelete && (
                <VStack align="stretch" spacing={2} mb={4}>
                  <Text>
                    <strong>Meeting Date:</strong>{' '}
                    {meetingToDelete.timestamp instanceof Timestamp
                      ? meetingToDelete.timestamp.toDate().toLocaleDateString()
                      : new Date(meetingToDelete.timestamp).toLocaleDateString()}
                  </Text>
                </VStack>
              )}
              <Text>
                Are you sure? This action will permanently delete{' '}
                {meetingToDelete?.hasSummary
                  ? 'the generated summary'
                  : 'all meeting notes'}{' '}
                and related data. This cannot be undone.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}
